<!-- ChatMessageItem.vue -->
<script setup lang="ts">
import { defineProps, computed, h, ref, onMounted, onBeforeUnmount } from 'vue'
import type ChatMessage from '@/types/ChatMessage'
import PlayerName from '@/components/Objects/PlayerName.vue'
import { usePlayerRelationStore } from '@/stores/playerRelations'
import { useAllianceStore } from '@/stores/allianceStore'
import { useChatStore } from '@/stores/chatStore'
import PositionIcon from '@/components/Elements/Images/Icons/PositionIcon.vue'
import StyledImage from '@/components/Elements/Images/StyledImage.vue'
import { useGameStore } from '@/stores/gameStore'

/**
 * Props
 */
const props = defineProps<{ message: ChatMessage }>()

/**
 * Stores
 */
const playerRelationStore = usePlayerRelationStore()
const allianceStore = useAllianceStore()
const chatStore = useChatStore()
const gameStore = useGameStore()

/**
 * Live time update
 */
const currentTime = ref(Date.now())
let intervalId: ReturnType<typeof setInterval> | null = null

onMounted(() => {
  // Update every 10 seconds
  intervalId = setInterval(() => {
    currentTime.value = Date.now()
  }, 10000)
})

onBeforeUnmount(() => {
  if (intervalId) {
    clearInterval(intervalId)
  }
})

/**
 * Methods
 */
const toDate = (epoch: number) => new Date(epoch < 2_000_000_000 ? epoch * 1000 : epoch)

/**
 * Computed
 */
const localTime = computed(() => {
  const dt = toDate(props.message.dateCreated)
  const now = new Date(currentTime.value)
  const diffMs = now.getTime() - dt.getTime()
  const diffSecs = Math.floor(diffMs / 1000)
  const diffMins = Math.floor(diffSecs / 60)
  const diffHours = Math.floor(diffMins / 60)
  const diffDays = Math.floor(diffHours / 24)
  const diffWeeks = Math.floor(diffDays / 7)
  const diffMonths = Math.floor(diffDays / 30)

  // Less than 1 minute
  if (diffSecs < 60) {
    return `${diffSecs} sec${diffSecs !== 1 ? 's' : ''} ago`
  }

  // Less than 1 hour
  if (diffMins < 60) {
    return `${diffMins} min${diffMins !== 1 ? 's' : ''} ago`
  }

  // Less than 24 hours
  if (diffHours < 24) {
    return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`
  }

  // Yesterday
  const yesterday = new Date(now)
  yesterday.setDate(yesterday.getDate() - 1)
  if (dt.toDateString() === yesterday.toDateString()) {
    return 'yesterday'
  }

  // Within current week (show day name)
  if (diffDays < 7) {
    return dt.toLocaleDateString([], { weekday: 'long' }).toLowerCase()
  }

  // Last week
  if (diffDays < 14) {
    return 'last week'
  }

  // 2-3 weeks ago
  if (diffWeeks < 4) {
    return `${diffWeeks} week${diffWeeks !== 1 ? 's' : ''} ago`
  }

  // Months ago
  if (diffMonths >= 1) {
    return `${diffMonths} month${diffMonths !== 1 ? 's' : ''} ago`
  }

  // Fallback (shouldn't reach here)
  return dt.toLocaleDateString()
})

interface MessagePart {
  type: 'position' | 'html'
  text?: string
  x?: number
  y?: number
  content?: string
}

const MessageContent = {
  props: {
    message: {
      type: String,
      required: true
    }
  },
  setup(props: { message: string }) {
    const parts = computed(() => {
      return props.message.split(/(\d+x\d+)/g).map((part: string): MessagePart => {
        if (part.match(/^\d+x\d+$/)) {
          const [x, y] = part.split('x').map((n: string) => parseInt(n, 10))
          return { type: 'position', text: part, x, y }
        }
        return { type: 'html', content: part }
      })
    })

    return () =>
      h(
        'span',
        {},
        parts.value.map((part: MessagePart) => {
          if (part.type === 'position' && part.x !== undefined && part.y !== undefined) {
            return [
              h('span', {}, part.text),
              h(PositionIcon, { x: part.x, y: part.y, class: 'inline-block align-middle ms-1' })
            ]
          }
          return h('span', { innerHTML: part.content })
        })
      )
  }
}

/**
 * Get message image
 */
const messageImage = computed(() => {
  if (props.message.playerId > 0) {
    return 'https://galexion.com/cache?url=' + playerRelationStore.getProfileImageByPlayerId(props.message.playerId) + '&w=56&h=56'
  } else {
    // Use alliance image
    const alliance = allianceStore.getAlliance(gameStore.player!.allianceId)
    if (alliance && alliance.allianceImage) {
      return 'https://galexion.com/cache?url=' + alliance.allianceImage + '&w=56&h=56'
    }
    return '' // Return empty string if no alliance image is found
  }
})

/**
 * Get alliance name
 */
const allianceName = computed(() => {
    return allianceStore.getAlliance(gameStore.player!.allianceId)!.name
})

/**
 * Check if the message sender is muted
 */
const isPlayerMuted = computed(() => {
  if (props.message.playerId > 0) {
    return chatStore.isPlayerMuted(props.message.playerId)
  }
  return false
})

/**
 * Open image in modal/lightbox
 */
const openImageModal = () => {
  if (props.message.imageUrl) {
    window.open(props.message.imageUrl, '_blank')
  }
}
</script>

<template>
  <div class="flex mt-2 py-1 bg-metal-950 bg-opacity-15 rounded hover:bg-opacity-20">
    <div class="px-1 shrink-0 hidden md:block">
      <div class="w-14 h-14 mr-2 shrink-0">
        <StyledImage :src="messageImage" alt="Chat" size="full" />
      </div>
    </div>

    <div class="flex-grow text-metal-300 min-w-0">
      <div class="flex items-start justify-between">
        <span class="font-bold text-metal-600 pb-2 truncate w-52 md:w-auto">
          <PlayerName v-if="message.playerId > 0" :player-id="message.playerId" :show-patreon-icon="true" />
          <span v-else>{{ allianceName }}</span>
        </span>
        <span class="text-metal-600 shrink-0 mx-2">{{ localTime }}</span>
      </div>

      <p class="mt-1 break-all">
        <span v-if="isPlayerMuted" class="text-metal-500 italic">&lt;muted&gt;</span>
        <MessageContent v-else :message="message.message" />
      </p>

      <!-- Image display -->
      <div v-if="message.imageUrl && !isPlayerMuted" class="mt-2">
        <img
          :src="message.imageUrl"
          alt="Chat image"
          class="max-w-full md:max-w-md rounded cursor-pointer hover:opacity-90 transition-opacity"
          @click="openImageModal"
        />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
/* override styles if needed */
</style>
